import { useState, useEffect } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthUser {
  name: string;
  email: string;
  role: string;
  createdAt: Date;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Update last_login_at on any sign-in event (email, OAuth, etc.)
        if (event === 'SIGNED_IN' && session?.user) {
          supabase
            .from('profiles')
            .update({ last_login_at: new Date().toISOString() })
            .eq('user_id', session.user.id)
            .then();
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    // Clear state immediately to prevent stale session restoration
    setUser(null);
    setSession(null);
    await supabase.auth.signOut();
  };

  // Transform Supabase user to our app's user format
  const [dynamicRole, setDynamicRole] = useState<string>("user");

  useEffect(() => {
    if (!user) { setDynamicRole("user"); return; }
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        setDynamicRole(data?.role ?? "user");
      });
  }, [user]);

  const authUser: AuthUser | null = user ? {
    name: user.user_metadata?.full_name || user.email?.split("@")[0] || "User",
    email: user.email || "",
    role: dynamicRole,
    createdAt: new Date(user.created_at),
  } : null;

  return {
    user,
    session,
    authUser,
    loading,
    signOut,
    isAuthenticated: !!session,
  };
}